// ─────────────────────────────────────────────────────────────────────────────
// Ignore Codes Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the ignoreCodes slice.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { IPagination } from 'src/app/shared/models/page.model';
import { IgnoreCodeFilters, IIgnoreCode } from '../models/ignore-code.models';
import * as ignoreCodesActions from './ignore-codes.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface IgnoreCodesState {
  ignoreCodes: IIgnoreCode[];
  metadata: IPagination | null;
  loading: boolean;
  creating: boolean;
  updating: boolean;
  keyword: string;
  filters: IgnoreCodeFilters;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialIgnoreCodesState: IgnoreCodesState = {
  ignoreCodes: [],
  metadata: null,
  loading: false,
  creating: false,
  updating: false,
  keyword: '',
  filters: { customer_id: null, create_date_from: null, create_date_to: null },
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialIgnoreCodesState,
  on(ignoreCodesActions.LoadIgnoreCodes, (state) => ({
    ...state,
    loading: true,
  })),
  on(ignoreCodesActions.LoadIgnoreCodesSuccess, (state, action) => ({
    ...state,
    loading: false,
    ignoreCodes: action.response.data,
    metadata: action.response.metadata,
  })),
  on(ignoreCodesActions.LoadIgnoreCodesError, (state) => ({
    ...state,
    loading: false,
  })),
  on(ignoreCodesActions.SetIgnoreCodesLoader, (state, action) => ({
    ...state,
    loading: action.isLoading,
  })),
  on(ignoreCodesActions.CreateIgnoreCode, (state) => ({
    ...state,
    creating: true,
  })),
  on(ignoreCodesActions.CreateIgnoreCodeSuccess, (state, action) => ({
    ...state,
    creating: false,
    ignoreCodes: [action.ignoreCode, ...state.ignoreCodes],
  })),
  on(ignoreCodesActions.CreateIgnoreCodeError, (state) => ({
    ...state,
    creating: false,
  })),
  on(ignoreCodesActions.UpdateIgnoreCode, (state) => ({
    ...state,
    updating: true,
  })),
  on(ignoreCodesActions.UpdateIgnoreCodeSuccess, (state, action) => ({
    ...state,
    updating: false,
    ignoreCodes: state.ignoreCodes.map((row) => (row.id === action.ignoreCode.id ? action.ignoreCode : row)),
  })),
  on(ignoreCodesActions.UpdateIgnoreCodeError, (state) => ({
    ...state,
    updating: false,
  })),
  on(ignoreCodesActions.SetIgnoreCodeSearch, (state, action) => ({
    ...state,
    keyword: action.keyword,
  })),
  on(ignoreCodesActions.SetIgnoreCodeFilters, (state, action) => ({
    ...state,
    filters: action.filters,
  })),
);

export function ignoreCodesReducer(state: IgnoreCodesState | undefined, action: Action) {
  return featureReducer(state, action);
}
